import { useEffect, useMemo, useState } from "react";
import { FolderOpen, FolderX } from "lucide-react";
import { toast } from "sonner";
import {
  isFolderPickerSupported,
  getSavedFolderName,
  pickFolder,
  clearFolder,
  onFolderChange,
} from "@/lib/downloadFolder";
import { useT } from "@/lib/i18n";

// Detect the visitor's OS just once — used to render an OS-specific hint on how to
// open the download folder inside the browser (which cannot launch File Explorer /
// Finder itself due to the sandbox).
function detectOS() {
  if (typeof navigator === "undefined") return "other";
  const ua = `${navigator.userAgent || ""} ${navigator.platform || ""}`.toLowerCase();
  if (ua.includes("mac")) return "mac";
  if (ua.includes("win")) return "windows";
  if (ua.includes("linux") || ua.includes("x11")) return "linux";
  return "other";
}

/**
 * Scoped download-folder picker.
 *
 * Props:
 *  - scope: optional string — usually a clubId. When set, the folder is
 *    remembered per club (each club can have its own download destination).
 *  - compact: when true renders a small inline widget suitable for a page
 *    header instead of the header-style pill button.
 */
export default function DownloadFolderButton({ scope, compact = false }) {
  const t = useT();
  const supported = isFolderPickerSupported();
  const [name, setName] = useState(null);
  const [open, setOpen] = useState(false);
  const inIframe = typeof window !== "undefined" && window.self !== window.top;

  useEffect(() => {
    let alive = true;
    getSavedFolderName(scope).then((n) => { if (alive) setName(n); });
    const off = onFolderChange((n, s) => {
      if ((s || null) === (scope || null) && alive) setName(n);
    });
    return () => { alive = false; off?.(); };
  }, [scope]);

  useEffect(() => {
    if (!open) return;
    const h = (e) => {
      if (!e.target.closest?.("[data-testid='download-folder-menu']") &&
          !e.target.closest?.("[data-testid='download-folder-btn']")) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [open]);

  const handlePick = async () => {
    setOpen(false);
    if (!supported) {
      toast.error(t("Deze browser ondersteunt geen mapkeuze (gebruik Chrome/Edge)."));
      return;
    }
    try {
      const chosen = await pickFolder(scope);
      setName(chosen);
      toast.success(t("Map opgeslagen als {name}").replace("{name}", chosen));
    } catch (e) {
      if (e?.name === "AbortError") return;
      console.warn("pickFolder failed:", e?.name, e?.message, e);
      if (e?.message === "permission-denied") {
        toast.error(t("Toegang tot map geweigerd"));
      } else if (e?.name === "SecurityError" || e?.name === "NotAllowedError") {
        toast.error(t("Deze functie werkt niet in de preview. Na publicatie werkt het gewoon in Edge/Chrome."));
      } else {
        toast.error(t("Mapkeuze mislukt: {msg}").replace("{msg}", e?.message || e?.name || "onbekend"));
      }
    }
  };

  const handleClear = async () => {
    setOpen(false);
    await clearFolder(scope);
    setName(null);
    toast.success(t("Map wissen gelukt"));
  };

  const label = name
    ? t("Map ingesteld: {name}").replace("{name}", name)
    : t("Geen map ingesteld");

  // OS-specifieke hint: browsers mogen zelf geen Verkenner/Finder openen, dus
  // we tonen de gebruiker de snelste sneltoets voor het huidige OS.
  const os = useMemo(() => detectOS(), []);
  const openHint = useMemo(() => {
    if (!name) return "";
    if (os === "windows") {
      return t("Openen: druk Win + E, typ dan {name} in de zoekbalk").replace("{name}", name);
    }
    if (os === "mac") {
      return t("Openen: druk ⌘ + spatie (Spotlight), typ dan {name}").replace("{name}", name);
    }
    if (os === "linux") {
      return t("Openen: druk Super/Meta, typ dan {name} in de zoekbalk").replace("{name}", name);
    }
    return t("Openen: gebruik je bestandsbeheerder en zoek {name}").replace("{name}", name);
  }, [os, name, t]);

  // Tooltip = huidige labelmet één regel witruimte + de OS-instructie zodat
  // hover meteen laat zien hoe de gebruiker de map opent.
  const tooltip = openHint ? `${label}\n\n${openHint}` : label;

  return (
    <div className="relative">
      <button
        onClick={() => setOpen((v) => !v)}
        data-testid="download-folder-btn"
        title={tooltip}
        className={`btn-brutal ${compact ? "h-10 px-3 text-xs" : "h-10 px-3 text-sm"} font-display uppercase font-bold inline-flex items-center gap-2 max-w-[240px] ${
          name ? "bg-primary text-primary-foreground" : "bg-foreground text-background hover:bg-foreground/90"
        }`}
      >
        <FolderOpen className="w-4 h-4 shrink-0" />
        <span className={`truncate ${compact ? "hidden sm:inline" : ""}`}>{name || t("Downloadmap")}</span>
      </button>
      {open && (
        <div
          data-testid="download-folder-menu"
          className="absolute right-0 mt-2 w-72 bg-card border-2 border-foreground heavy-shadow z-50 p-3 space-y-2"
        >
          <div className="stat-label">{label}</div>
          {name && (
            <div
              data-testid="download-folder-open-hint"
              className="text-xs text-foreground/70 leading-snug"
            >
              {openHint}
            </div>
          )}
          {!supported && (
            <div className="text-xs text-destructive font-mono-num">
              {t("Deze browser ondersteunt geen mapkeuze (gebruik Chrome/Edge).")}
            </div>
          )}
          {supported && inIframe && (
            <div className="text-xs text-destructive" data-testid="folder-iframe-warning">
              {t("Deze functie werkt niet in de preview. Na publicatie werkt het gewoon in Edge/Chrome.")}
            </div>
          )}
          <button
            onClick={handlePick}
            disabled={!supported}
            data-testid="download-folder-pick"
            className="btn-brutal w-full bg-primary text-primary-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            <FolderOpen className="w-4 h-4" /> {t("Map kiezen")}
          </button>
          {name && (
            <button
              onClick={handleClear}
              data-testid="download-folder-clear"
              className="btn-brutal w-full bg-background text-foreground px-3 py-2 font-display uppercase font-bold text-sm inline-flex items-center gap-2"
            >
              <FolderX className="w-4 h-4" /> {t("Map wissen")}
            </button>
          )}
        </div>
      )}
    </div>
  );
}
